// src/components/agents/agent-runner.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  X, Play, Download, CheckCircle2, AlertCircle,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus,
  Loader2, Star, AlertTriangle, Lightbulb, Eye,
} from "lucide-react";
import {
  Agent, AgentAggregatedResult, AgentModelResult,
  openAgentRunSocket, AgentWsEvent,
} from "@/app/services/agents/agents-api";
import { toast } from "sonner";

type ModelStatus = "queued" | "running" | "done" | "error";

interface AgentRunnerProps {
  agent: Agent;
  brandNameOverride?: string;
  onClose: () => void;
  onRunSaved: (runId: number) => void;
}

function ScoreGauge({ score }: { score: number }) {
  const color =
    score >= 75 ? "var(--accent)"
    : score >= 50 ? "var(--alpha-teal)"
    : "var(--infrared-coral)";
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div
      className="relative flex items-center justify-center flex-shrink-0"
      style={{ width: 140, height: 140 }}
    >
      <svg
        width="140"
        height="140"
        viewBox="0 0 140 140"
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle cx="70" cy="70" r="54" fill="none" stroke="var(--muted)" strokeWidth="10" />
        <circle
          cx="70" cy="70" r="54" fill="none"
          stroke={color} strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span style={{ fontSize: "36px", fontWeight: 700, color, lineHeight: 1 }}>
          {Math.round(score)}
        </span>
        <span
          style={{
            fontSize: "var(--text-xs)",
            color: "var(--muted-foreground)",
            marginTop: "2px",
          }}
        >
          /100
        </span>
      </div>
    </div>
  );
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const s = (sentiment || "").toLowerCase();
  const { icon: Icon, color } =
    s === "positive"
      ? { icon: TrendingUp, color: "var(--accent)" }
      : s === "negative"
      ? { icon: TrendingDown, color: "var(--infrared-coral)" }
      : { icon: Minus, color: "var(--muted-foreground)" };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap"
      style={{
        backgroundColor: `${color}15`,
        color,
        border: `1px solid ${color}30`,
      }}
    >
      <Icon className="w-3 h-3" />
      {s || "neutral"}
    </span>
  );
}

export function AgentRunner({
  agent,
  brandNameOverride,
  onClose,
  onRunSaved,
}: AgentRunnerProps) {
  const brand =
    brandNameOverride ||
    agent.brand_name ||
    localStorage.getItem("bm_brand") ||
    "";
  const [brandInput, setBrandInput] = useState(brand);

  const [phase, setPhase] = useState<"config" | "running" | "results">("config");
  const [progress, setProgress] = useState(0);
  const [modelStatuses, setModelStatuses] = useState<Record<string, ModelStatus>>({});
  const [finalData, setFinalData] = useState<{
    per_model_results: Record<string, AgentModelResult>;
    aggregated: AgentAggregatedResult;
  } | null>(null);
  const [runId, setRunId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expandedModel, setExpandedModel] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);

  const handleRun = useCallback(() => {
    if (!brandInput.trim()) { alert("Brand name is required"); return; }

    const initial: Record<string, ModelStatus> = {};
    agent.selected_models.forEach((m) => (initial[m] = "queued"));
    setModelStatuses(initial);
    setProgress(0);
    setFinalData(null);
    setErrors({});
    setErrorMsg(null);
    setPhase("running");

    wsRef.current?.close();
    wsRef.current = openAgentRunSocket(
      {
        agent_id: agent.id,
        agent_name: agent.name,
        brand_name: brandInput.trim(),
        prompt: agent.prompt,
        models: agent.selected_models,
        run_options: agent.run_options,
      },
      (evt: AgentWsEvent) => {
        if (evt.type === "model_status") {
          setModelStatuses((prev) => ({ ...prev, [evt.engine]: evt.status as ModelStatus }));
        } else if (evt.type === "progress") {
          setProgress(evt.percent);
        } else if (evt.type === "final_result") {
          setFinalData(evt.data);
          setErrors(evt.errors || {});
          setRunId(evt.run_id);
          onRunSaved(evt.run_id);
          if (evt.email_status === "sent" && evt.email_to) {
            toast.success(`Results emailed to ${evt.email_to}`);
          } else if (evt.email_status === "blocked") {
            // Refused by the recipient allow-list or the daily cap. The run
            // itself succeeded, so this is a warning, not a run failure.
            toast.warning(evt.email_error || "Results not emailed — recipient not approved");
          } else if (evt.email_status === "failed") {
            toast.error("Email delivery failed — check server SMTP configuration");
          } else if (evt.email_status === "not_configured") {
            toast.warning("Email not sent — SMTP is not configured on the server");
          }
          setPhase("results");
        } else if (evt.type === "error") {
          setErrorMsg(evt.message || "Run failed");
          setPhase("config");
        }
      },
      () => { setErrorMsg("Connection error"); setPhase("config"); },
      () => {},
    );
  }, [agent, brandInput, onRunSaved]);

  useEffect(() => () => { wsRef.current?.close(); }, []);

  const handleDownload = () => {
    if (!finalData) return;
    const data = {
      agent_name: agent.name,
      brand_name: brandInput,
      prompt: agent.prompt,
      run_id: runId,
      timestamp: new Date().toISOString(),
      aggregated: finalData.aggregated,
      per_model_results: finalData.per_model_results,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agent-run-${agent.name.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4"
      style={{
        backgroundColor: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="relative w-full overflow-y-auto rounded-2xl"
        style={{
          backgroundColor: "var(--background)",
          border: "1px solid var(--border)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
          maxWidth: "min(48rem, calc(100dvw - 24px))",
          maxHeight: "min(92dvh, 100dvh - 24px)",
        }}
      >
        {/* Header — stacks on mobile so 3 items don't crowd */}
        <div
          className="sticky top-0 z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 md:px-6 py-4 border-b"
          style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
          }}
        >
          <div className="min-w-0 flex-1">
            <h2
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {phase === "running"
                ? "Running Workflow…"
                : phase === "results"
                ? "Workflow Results"
                : `Run: ${agent.name}`}
            </h2>
            <p
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--muted-foreground)",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {brandInput && `Brand: ${brandInput}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
            {phase === "results" && agent.run_options.enable_download && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap"
                style={{
                  backgroundColor: "var(--muted)",
                  border: "1px solid var(--border)",
                  color: "var(--foreground)",
                  cursor: "pointer",
                }}
              >
                <Download className="w-4 h-4" />
                Export JSON
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              style={{
                color: "var(--muted-foreground)",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-5 md:space-y-6">
          {/* ── Phase: Config ── */}
          {phase === "config" && (
            <div className="space-y-4 md:space-y-5">
              {errorMsg && (
                <div
                  className="flex items-start gap-3 p-4 rounded-xl"
                  style={{
                    backgroundColor: "var(--error-bg)",
                    border: "1px solid var(--error-border)",
                  }}
                >
                  <AlertCircle
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: "var(--error-text)" }}
                  />
                  <p
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--error-text)",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {errorMsg}
                  </p>
                </div>
              )}

              {/* Brand Override */}
              <div>
                <label
                  className="block mb-2"
                  style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}
                >
                  Brand Name
                </label>
                <input
                  value={brandInput}
                  onChange={(e) => setBrandInput(e.target.value)}
                  placeholder="Brand to analyse…"
                  className="w-full px-4 py-3 rounded-xl border outline-none"
                  style={{
                    backgroundColor: "var(--muted)",
                    borderColor: "var(--border)",
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                  }}
                />
                <p
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--muted-foreground)",
                    marginTop: "6px",
                  }}
                >
                  Override the brand context for this run only.
                </p>
              </div>

              {/* Prompt preview */}
              <div>
                <label
                  className="block mb-2"
                  style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}
                >
                  Prompt
                </label>
                <div
                  className="p-3 md:p-4 rounded-xl border"
                  style={{
                    backgroundColor: "var(--muted)",
                    borderColor: "var(--border)",
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    lineHeight: 1.7,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {agent.prompt}
                </div>
              </div>

              {/* Models */}
              <div>
                <label
                  className="block mb-2"
                  style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}
                >
                  Models
                </label>
                <div className="flex flex-wrap gap-2">
                  {agent.selected_models.map((m) => (
                    <span
                      key={m}
                      className="px-3 py-1.5 rounded-full text-xs font-medium capitalize"
                      style={{
                        backgroundColor: "var(--alpha-teal-10)",
                        color: "var(--alpha-teal)",
                        border: "1px solid var(--alpha-teal-20)",
                      }}
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>

              <button
                onClick={handleRun}
                className="w-full flex items-center justify-center gap-2 py-3 md:py-3.5 rounded-xl font-semibold text-sm transition-all"
                style={{
                  backgroundColor: "var(--alpha-teal)",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Play className="w-4 h-4" />
                Run Workflow
              </button>
            </div>
          )}

          {/* ── Phase: Running ── */}
          {phase === "running" && (
            <div className="space-y-5 md:space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    Overall Progress
                  </span>
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {Math.round(progress)}%
                  </span>
                </div>
                <div
                  className="h-2 rounded-full"
                  style={{ backgroundColor: "var(--muted)" }}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${progress}%`,
                      backgroundColor: "var(--alpha-teal)",
                    }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {agent.selected_models.map((m) => {
                  const status = modelStatuses[m] || "queued";
                  return (
                    <div
                      key={m}
                      className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border"
                      style={{
                        backgroundColor: "var(--muted-10)",
                        borderColor: "var(--border)",
                      }}
                    >
                      {status === "queued" && (
                        <div
                          className="w-5 h-5 rounded-full border-2 flex-shrink-0"
                          style={{ borderColor: "var(--border)" }}
                        />
                      )}
                      {status === "running" && (
                        <Loader2
                          className="w-5 h-5 animate-spin flex-shrink-0"
                          style={{ color: "var(--alpha-teal)" }}
                        />
                      )}
                      {status === "done" && (
                        <CheckCircle2
                          className="w-5 h-5 flex-shrink-0"
                          style={{ color: "var(--accent)" }}
                        />
                      )}
                      {status === "error" && (
                        <AlertCircle
                          className="w-5 h-5 flex-shrink-0"
                          style={{ color: "var(--infrared-coral)" }}
                        />
                      )}
                      <span
                        className="flex-1 min-w-0"
                        style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {m}
                      </span>
                      <span
                        className="text-xs px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap"
                        style={{
                          backgroundColor:
                            status === "done"   ? "var(--success-bg)"
                            : status === "error"   ? "var(--error-bg)"
                            : status === "running" ? "var(--alpha-teal-10)"
                            : "var(--muted)",
                          color:
                            status === "done"   ? "var(--success-text)"
                            : status === "error"   ? "var(--error-text)"
                            : status === "running" ? "var(--alpha-teal)"
                            : "var(--muted-foreground)",
                        }}
                      >
                        {status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Phase: Results ── */}
          {phase === "results" && finalData && (
            <div className="space-y-5 md:space-y-6">
              {/* Aggregated hero */}
              <div
                className="p-4 md:p-6 rounded-2xl border"
                style={{
                  backgroundColor: "var(--muted-10)",
                  borderColor: "var(--border)",
                }}
              >
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                  <ScoreGauge score={finalData.aggregated.overall_perception_score} />
                  <div className="flex-1 min-w-0 space-y-4 w-full">
                    <div>
                      <div className="flex items-center gap-2 md:gap-3 mb-1 flex-wrap">
                        <h3 style={{ fontSize: "var(--text-lg)", fontWeight: 700 }}>
                          Perception Score
                        </h3>
                        <SentimentBadge sentiment={finalData.aggregated.sentiment} />
                      </div>
                      <p
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--muted-foreground)",
                          lineHeight: 1.6,
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {finalData.aggregated.consensus_summary}
                      </p>
                    </div>
                    {/* Model scores — 1 col mobile, 3 cols sm+ */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                      {Object.entries(finalData.aggregated.model_scores).map(([model, score]) => (
                        <div
                          key={model}
                          className="text-center p-3 rounded-xl"
                          style={{
                            backgroundColor: "var(--background)",
                            border: "1px solid var(--border)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "var(--text-xl)",
                              fontWeight: 700,
                              color: "var(--alpha-teal)",
                            }}
                          >
                            {Math.round(score)}
                          </div>
                          <div
                            style={{
                              fontSize: "var(--text-xs)",
                              color: "var(--muted-foreground)",
                              textTransform: "capitalize",
                            }}
                          >
                            {model}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Key findings */}
              {finalData.aggregated.key_findings.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 mb-3" style={{ fontWeight: 600 }}>
                    <Eye
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--alpha-teal)" }}
                    />
                    Key Findings
                  </h4>
                  <div className="space-y-2">
                    {finalData.aggregated.key_findings.map((f, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-3 rounded-xl"
                        style={{
                          backgroundColor: "var(--muted)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <span
                          style={{
                            color: "var(--alpha-teal)",
                            fontWeight: 700,
                            fontSize: "var(--text-sm)",
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}.
                        </span>
                        <span
                          style={{
                            fontSize: "var(--text-sm)",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {f}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths / Weaknesses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <h4 className="flex items-center gap-2 mb-3" style={{ fontWeight: 600 }}>
                    <Star
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--accent)" }}
                    />
                    Strengths
                  </h4>
                  <div className="space-y-2">
                    {finalData.aggregated.combined_strengths.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-3 rounded-xl"
                        style={{
                          backgroundColor: "var(--muted)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: "var(--accent)" }}
                        />
                        <span
                          style={{
                            fontSize: "var(--text-sm)",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {s}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="flex items-center gap-2 mb-3" style={{ fontWeight: 600 }}>
                    <AlertTriangle
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--infrared-coral)" }}
                    />
                    Weaknesses
                  </h4>
                  <div className="space-y-2">
                    {finalData.aggregated.combined_weaknesses.map((w, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-3 rounded-xl"
                        style={{
                          backgroundColor: "var(--muted)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: "var(--infrared-coral)" }}
                        />
                        <span
                          style={{
                            fontSize: "var(--text-sm)",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {w}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {finalData.aggregated.top_recommendations.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 mb-3" style={{ fontWeight: 600 }}>
                    <Lightbulb
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--alpha-teal)" }}
                    />
                    Recommendations
                  </h4>
                  <div className="space-y-3">
                    {finalData.aggregated.top_recommendations.map((r, i) => (
                      <div
                        key={i}
                        className="p-3 md:p-4 rounded-xl border"
                        style={{
                          backgroundColor: "var(--muted)",
                          borderColor: "var(--border)",
                        }}
                      >
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <span
                            style={{
                              fontSize: "var(--text-sm)",
                              fontWeight: 600,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {r.title}
                          </span>
                          <span
                            className="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 whitespace-nowrap"
                            style={{
                              backgroundColor:
                                r.priority === "HIGH"   ? "var(--error-bg)"
                                : r.priority === "MEDIUM" ? "var(--warning-bg)"
                                : "var(--muted)",
                              color:
                                r.priority === "HIGH"   ? "var(--error-text)"
                                : r.priority === "MEDIUM" ? "var(--warning-text)"
                                : "var(--muted-foreground)",
                            }}
                          >
                            {r.priority}
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--muted-foreground)",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {r.description}
                        </p>
                        <div
                          className="mt-2 text-xs"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          Impact:{" "}
                          <span style={{ fontWeight: 600, color: "var(--alpha-teal)" }}>
                            {r.impact}/100
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-model breakdown */}
              <div>
                <h4 className="mb-3" style={{ fontWeight: 600 }}>
                  Per-Model Breakdown
                </h4>
                <div className="space-y-3">
                  {Object.entries(finalData.per_model_results).map(([model, result]) => (
                    <div
                      key={model}
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <button
                        onClick={() =>
                          setExpandedModel((prev) => (prev === model ? null : model))
                        }
                        className="w-full flex items-center justify-between gap-3 p-3 md:p-4"
                        style={{
                          backgroundColor: "var(--muted)",
                          textAlign: "left",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <div className="flex items-center gap-2 md:gap-3 flex-wrap min-w-0">
                          <span
                            style={{
                              fontSize: "var(--text-sm)",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          >
                            {model}
                          </span>
                          <SentimentBadge sentiment={result.sentiment} />
                        </div>
                        <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                          <span
                            className="whitespace-nowrap"
                            style={{
                              fontWeight: 700,
                              color: "var(--alpha-teal)",
                              fontSize: "var(--text-sm)",
                            }}
                          >
                            {Math.round(result.perception_score)}/100
                          </span>
                          {expandedModel === model ? (
                            <ChevronUp
                              className="w-4 h-4"
                              style={{ color: "var(--muted-foreground)" }}
                            />
                          ) : (
                            <ChevronDown
                              className="w-4 h-4"
                              style={{ color: "var(--muted-foreground)" }}
                            />
                          )}
                        </div>
                      </button>
                      {expandedModel === model && (
                        <div
                          className="p-3 md:p-4 space-y-4 border-t"
                          style={{ borderColor: "var(--border)" }}
                        >
                          <p
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "var(--muted-foreground)",
                              lineHeight: 1.7,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {result.summary}
                          </p>
                          {result.raw_analysis && (
                            <div
                              className="p-3 md:p-4 rounded-xl"
                              style={{
                                backgroundColor: "var(--muted-10)",
                                border: "1px solid var(--border)",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "var(--text-sm)",
                                  lineHeight: 1.7,
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {result.raw_analysis}
                              </p>
                            </div>
                          )}
                          {result.data_sources?.length > 0 && (
                            <div>
                              <p
                                style={{
                                  fontSize: "var(--text-xs)",
                                  color: "var(--muted-foreground)",
                                  fontWeight: 600,
                                  marginBottom: "6px",
                                }}
                              >
                                SOURCES
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {result.data_sources.map((s, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-1 rounded text-xs"
                                    style={{
                                      backgroundColor: "var(--muted)",
                                      border: "1px solid var(--border)",
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Error summary */}
              {Object.keys(errors).length > 0 && (
                <div
                  className="p-3 md:p-4 rounded-xl"
                  style={{
                    backgroundColor: "var(--error-bg)",
                    border: "1px solid var(--error-border)",
                  }}
                >
                  <p
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: 600,
                      color: "var(--error-text)",
                      marginBottom: "6px",
                    }}
                  >
                    {Object.keys(errors).length} model(s) failed
                  </p>
                  {Object.entries(errors).map(([m, msg]) => (
                    <p
                      key={m}
                      style={{
                        fontSize: "var(--text-xs)",
                        color: "var(--error-text)",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      <strong>{m}:</strong> {msg}
                    </p>
                  ))}
                </div>
              )}

              <button
                onClick={handleRun}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm border transition-all"
                style={{
                  backgroundColor: "var(--muted)",
                  borderColor: "var(--border)",
                  color: "var(--foreground)",
                  cursor: "pointer",
                }}
              >
                <Play className="w-4 h-4" />
                Run Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}